import { isEmpty, isEqual } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { useHistory } from 'react-router-dom'

import { getCurrentUserRoles } from '@/components/App/selectors'
import { Button } from '@/components/Button'
import { useNotification } from '@/hooks/useNotification'
import { useAppSelector } from '@/store/hooks'

export enum Role {
  ORGANIZATION_ADMIN_ROLE = 'TenantAdmin',
  SPACE_ADMIN_ROLE = 'SpaceAdmin',
  PUBLISHER_ROLE = 'Publisher',
  PUBLISHER_ADMIN_ROLE = 'PublisherAdmin',
  CHATBOT_ADMIN_ROLE = 'ChatbotAdmin',
  CHATBOT_AUDITOR_ROLE = 'ChatbotAuditor',
  WORKSHOP_MANAGER_ROLE = 'WorkshopManager',
  WORKSHOP_APP_OWNER = 'WorkshopAppOwner',
  ANALYTICS_READER_ROLE = 'AnalyticsReader'
}

type ResourceRoles = { id: string; name: Role }[]

type UserRoles = {
  organization: Record<string, ResourceRoles>
  spaces: Record<string, ResourceRoles>
}

export function useRoles() {
  const history = useHistory()
  const { notify } = useNotification()

  const userQuery = useQuery<{ data: { roles: UserRoles } }>({ queryKey: '/v1/admin/user', refetchInterval: 30_000 })

  const latestUserRoles = userQuery.data?.data.roles

  const userRoles = useAppSelector(getCurrentUserRoles) as UserRoles

  const [notified, setNotified] = useState(false)

  useEffect(() => {
    const userRolesIsEmpty = isEmpty(userRoles.organization) && isEmpty(userRoles.spaces)
    const latestUserRolesIsEmpty = isEmpty(latestUserRoles?.organization) && isEmpty(latestUserRoles?.spaces)

    if (userRolesIsEmpty || latestUserRolesIsEmpty || isEqual(userRoles, latestUserRoles) || notified) {
      return
    }

    notify({
      variant: 'warning',
      title: 'Your user permissions have changed',
      duration: '24 hours',
      body: (
        <div className="space-y-2">
          <p>A space admin has updated your user roles. Please log out and back in for the role changes to apply.</p>
          <div className="flex justify-end">
            <Button color="primary" onClick={() => history.push('/logout', { updatedSpaceRoles: true })}>
              Log out
            </Button>
          </div>
        </div>
      )
    })

    setNotified(true)
  }, [userRoles, latestUserRoles, notify, history, notified])

  const isAllowedAccess = (roles?: Role[], organizationId?: string, spaceId?: string) => {
    if (!roles) {
      // if no role is required, allow access
      return true
    }

    return !!userRoles && (hasOrganizationAccess(roles, organizationId) || hasSpaceAccess(roles, spaceId))
  }

  const hasOrganizationAdminRole = (organizationId: string) => {
    return hasOrganizationAccess([Role.ORGANIZATION_ADMIN_ROLE], organizationId)
  }

  const hasChatbotAdminRole = (spaceId: string) => hasSpaceRole(spaceId, Role.CHATBOT_ADMIN_ROLE)

  const hasChatbotAuditorRole = (spaceId: string) => hasSpaceRole(spaceId, Role.CHATBOT_AUDITOR_ROLE)

  const hasPublisherAdminRole = (spaceId: string) => hasSpaceRole(spaceId, Role.PUBLISHER_ADMIN_ROLE)

  const hasSpaceAdminRole = (spaceId: string) => hasSpaceRole(spaceId, Role.SPACE_ADMIN_ROLE)

  const hasWorkshopManagerRole = (spaceId: string) => hasSpaceRole(spaceId, Role.WORKSHOP_MANAGER_ROLE)

  const hasWorkshopAppOwnerRole = (spaceId: string) => hasSpaceRole(spaceId, Role.WORKSHOP_APP_OWNER)

  const hasSpaceRole = (spaceId: string, roleName: Role) => {
    return (
      !!userRoles.spaces &&
      !!userRoles.spaces[spaceId] &&
      userRoles.spaces[spaceId].map(({ name }) => name).includes(roleName)
    )
  }

  const hasAnyRoleInSpace = (spaceId: string) => {
    return !!userRoles.spaces && !!userRoles.spaces[spaceId] && userRoles.spaces[spaceId].length > 0
  }

  const hasAnyRoleInAnySpace = (spaces: { id: string }[]) => {
    return spaces.some(({ id }) => hasAnyRoleInSpace(id))
  }

  const hasOrganizationAccess = (roles: Role[], organizationId?: string) => {
    if (!organizationId) return false

    if (!userRoles.organization) return false

    const rolesInOrganization = userRoles.organization[organizationId]

    return rolesInOrganization ? hasRole(roles, rolesInOrganization) : false
  }

  const hasSpaceAccess = (roles: Role[], spaceId?: string) => {
    if (!spaceId) return false

    const rolesInSpace = userRoles.spaces[spaceId]
    return rolesInSpace ? hasRole(roles, rolesInSpace) : false
  }

  const hasRole = (allowedRoleNames: Role[], usersRolesInResource: ResourceRoles) => {
    return !!usersRolesInResource.some(({ name }) => allowedRoleNames.includes(name))
  }

  return useMemo(
    () => ({
      isAllowedAccess,
      hasOrganizationAdminRole,
      hasAnyRoleInSpace,
      hasAnyRoleInAnySpace,
      hasPublisherAdminRole,
      hasChatbotAdminRole,
      hasChatbotAuditorRole,
      hasSpaceRole,
      hasSpaceAdminRole,
      hasWorkshopManagerRole,
      hasWorkshopAppOwnerRole
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userRoles]
  )
}
